// src/components/shell/GlobalSearch.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

/** Where the library reads its query from, so a search can be linked to. */
export const QUERY_PARAM = 'q';

/**
 * One search box, in the app shell.
 *
 * Search used to be a 288px field inside the filter rail — on the library page
 * only, and visually competing with a toolbar magnifier that meant "search by a
 * file's CONTENT". Here it is the same on every page: type from Settings and
 * you land in the library with the results.
 *
 * The query is kept in the URL rather than in component state, which is what
 * lets one box drive the library from elsewhere — and makes a search shareable.
 */
export default function GlobalSearch() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const inputRef = useRef(null);

    const onLibrary = location.pathname === '/';
    const urlQuery = onLibrary ? (searchParams.get(QUERY_PARAM) ?? '') : '';
    const [draft, setDraft] = useState(urlQuery);

    // Follow the URL when something else changes it (a command-palette search,
    // the back button, "Clear all" in the filter panel).
    useEffect(() => { setDraft(urlQuery); }, [urlQuery]);

    const commit = (value) => {
        if (onLibrary) {
            const next = new URLSearchParams(searchParams);
            if (value) next.set(QUERY_PARAM, value); else next.delete(QUERY_PARAM);
            // `replace`, so typing doesn't bury the previous page under a
            // history entry per keystroke.
            setSearchParams(next, { replace: true });
        } else if (value) {
            navigate(`/?${QUERY_PARAM}=${encodeURIComponent(value)}`);
        } else {
            navigate('/');
        }
    };

    const onChange = (e) => {
        setDraft(e.target.value);
        // Off the library page, wait for Enter: nobody wants to be yanked out
        // of Settings by their first keystroke.
        if (onLibrary) commit(e.target.value);
    };

    return (
        <form
            className="qd-search"
            role="search"
            onSubmit={(e) => { e.preventDefault(); commit(draft); }}
        >
            <span className="qd-search__icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
            </span>
            <input
                ref={inputRef}
                id="qd-global-search"
                className="qd-search__input"
                type="search"
                value={draft}
                onChange={onChange}
                placeholder="Search files"
                aria-label="Search files"
            />
            {draft ? (
                <button
                    type="button"
                    className="qd-search__clear"
                    aria-label="Clear search"
                    onClick={() => { setDraft(''); commit(''); inputRef.current?.focus(); }}
                >
                    ×
                </button>
            ) : (
                <kbd className="qd-search__hint" aria-hidden="true">/</kbd>
            )}
        </form>
    );
}
